'use client';

import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button } from '@eventbrite/marmalade';
import { trackAnalyticsEvent } from '@eventbrite/legacy-infra.site-analytics';
import { CheckoutButton } from '../CheckoutButton/CheckoutButton';
import { RSVPModal } from './RSVPModal';
import { RSVPResponseType } from './types';
import { useLiveEventData } from '../hooks/useLiveEventData';
import { useComponentVisibility } from '@/src/listings/hooks/useComponentVisibility';
import { GACodes, LISTING_HEAP_IDS } from '@/src/listings/components/_shared/constants';
import styles from './RSVPPanel.module.scss';

export interface RSVPPanelProps {
  mipaToken: string | null;
}

interface RsvpModalState {
  open: boolean;
  responseType: RSVPResponseType | null;
}

export const RSVPPanel: React.FC<RSVPPanelProps> = ({ mipaToken }) => {
  const { eventId } = useLiveEventData();
  const [modalState, setModalState] = useState<RsvpModalState>({
    open: false,
    responseType: null,
  });

  const onRsvpSectionVisible = useCallback(() => {
    trackAnalyticsEvent({
      category: GACodes.GA_CATEGORY_LISTING,
      action: GACodes.GA_ACTION_RSVP_LOADED,
    });
  }, []);
  const rsvpSectionRef = useComponentVisibility(onRsvpSectionVisible);

  const openModal = (responseType: RSVPResponseType) => setModalState({ open: true, responseType });
  const closeModal = () => setModalState({ open: false, responseType: null });

  return (
    <div ref={rsvpSectionRef} className={styles.container} data-testid="rsvp-panel">
      <div className={styles.content}>
        <div className={styles.label} data-testid="rsvp-label">
          <FormattedMessage
            id="Are you going to this event?"
            defaultMessage="Are you going to this event?"
            description="RSVP label in conversion bar"
          />
        </div>
      </div>
      <div data-testid="cb-cta-area" className={styles.ctaButtonWrapper}>
        <CheckoutButton isRemindMe={false} isRSVP={true} />
        <Button
          variant="secondary"
          onClick={() => openModal('maybe')}
          data-heap-id={LISTING_HEAP_IDS.CTA_RSVP_MAYBE}
          data-testid="rsvp-maybe-button"
          className={styles.secondaryRSVPButton}
        >
          <FormattedMessage id="Maybe" defaultMessage="Maybe" description="RSVP Maybe button" />
        </Button>
        <Button
          variant="secondary"
          onClick={() => openModal('no')}
          data-heap-id={LISTING_HEAP_IDS.CTA_RSVP_NO}
          data-testid="rsvp-no-button"
          className={styles.secondaryRSVPButton}
        >
          <FormattedMessage id="No" defaultMessage="No" description="RSVP No button" />
        </Button>
      </div>
      {modalState.responseType && (
        <RSVPModal
          visible={modalState.open}
          onClose={closeModal}
          responseType={modalState.responseType}
          eventId={eventId ?? ''}
          mipaToken={mipaToken}
        />
      )}
    </div>
  );
};
